import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFilters } from '../contexts/FilterContext';
import { useAnalyticsCache } from '../contexts/AnalyticsCacheContext';
import { API, triggerMapRefresh } from '../utils/api';
import { extractHashtags } from '../utils/hashtags';
import useXHR from './useXHR';

const CATEGORY_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6',
  '#FFC107', '#FFD54F', '#a855f7', '#d946ef', '#FFD700', '#f43f5e'
];

const useSearchPageData = (showToast) => {
  const { token, user } = useAuth();
  const { updateCache: updateAnalyticsCache } = useAnalyticsCache();
  const xhr = useXHR(token);
  
  // Pull all filter state from shared FilterContext
  const filters = useFilters();
  const {
    educatedIndividualFilters, setEducatedIndividualFilters,
    eiMinWords, setEiMinWords,
    minWordCount, setMinWordCount,
    documentTypes, selectedDocTypes, setSelectedDocTypes,
    docTypeMinWords, setDocTypeMinWords,
    certificationFilters, setCertificationFilters,
    certMinWords, setCertMinWords,
    contentMinWords, setContentMinWords,
    minQualityScore, setMinQualityScore,
    exactMatch, setExactMatch,
    searchDepth, setSearchDepth,
    aggregation, setAggregation,
    selectedCategories, setSelectedCategories,
    showControlPanel, setShowControlPanel,
    controlPanelLayout, setControlPanelLayout,
    filterHistory, getRecommendations,
    loadPreferences, exportFilters,
    undo, redo, canUndo, canRedo
  } = filters;
  
  // Core state
  const [categories, setCategories] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [categorizeLoading, setCategorizeLoading] = useState(false);
  const [showProgrammerMode, setShowProgrammerMode] = useState(false);
  
  // Auto-update toggle for URL Collection Windows (default OFF)
  const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(false);
  
  // Modal state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', protocol: '', parent_id: null, privacy: 'public' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editProtocol, setEditProtocol] = useState('');
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editPrivacy, setEditPrivacy] = useState('private');
  const [shareCategory, setShareCategory] = useState(null); // For ShareCategoryModal
  
  // UI toggles
  const [batches, setBatches] = useState([]);
  const [showBatchManager, setShowBatchManager] = useState(false);
  const [lastBatchId, setLastBatchId] = useState(null);
  const [showDebugger, setShowDebugger] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [showContentDump, setShowContentDump] = useState(false);
  
  // Search Windows state (max 60 windows)
  // Persisted across sessions via localStorage (up to 20 most-recent windows,
  // capped at 200 results each to stay within quota).
  const SEARCH_WINDOWS_STORAGE_KEY = 'infopilot.searchWindows.v1';
  const PERSIST_MAX_WINDOWS = 20;
  const PERSIST_MAX_RESULTS_PER_WINDOW = 200;

  const [searchWindows, setSearchWindows] = useState(() => {
    try {
      const raw = localStorage.getItem(SEARCH_WINDOWS_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Rehydrate: ensure each window still has required shape
      return parsed.filter(w => w && w.id && w.title && Array.isArray(w.results));
    } catch {
      return [];
    }
  });
  const MAX_WINDOWS = 60;

  // Persist searchWindows to localStorage (debounced, trimmed, quota-safe)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handle = setTimeout(() => {
      try {
        const trimmed = searchWindows.slice(0, PERSIST_MAX_WINDOWS).map(w => ({
          ...w,
          results: (w.results || []).slice(0, PERSIST_MAX_RESULTS_PER_WINDOW)
        }));
        localStorage.setItem(SEARCH_WINDOWS_STORAGE_KEY, JSON.stringify(trimmed));
      } catch (err) {
        // Quota exceeded — drop older windows until it fits
        try {
          const fallback = searchWindows.slice(0, 5).map(w => ({
            ...w,
            results: (w.results || []).slice(0, 50)
          }));
          localStorage.setItem(SEARCH_WINDOWS_STORAGE_KEY, JSON.stringify(fallback));
        } catch {
          // Give up silently — persistence is a best-effort enhancement
        }
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [searchWindows]);

  // Semantic Search (Elasticsearch) state
  const [esQuery, setEsQuery] = useState('');
  const [esResults, setEsResults] = useState([]);
  const [esLoading, setEsLoading] = useState(false);
  const [esTotal, setEsTotal] = useState(0);
  const [showSemanticSearch, setShowSemanticSearch] = useState(false);
  const [esSyncing, setEsSyncing] = useState(false);

  // Helper: Add a search result window, enforcing the 60 max limit
  const addSearchWindow = useCallback((title, searchType, results, accentColor = '#FFD54F') => {
    const newWindow = {
      id: `win-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      title,
      searchType,
      results: results || [],
      accentColor,
      createdAt: Date.now()
    };
    setSearchWindows(prev => {
      const updated = [newWindow, ...prev];
      // Enforce max 60 windows - remove oldest
      return updated.slice(0, MAX_WINDOWS);
    });
  }, [MAX_WINDOWS]);

  const closeSearchWindow = useCallback((windowId) => {
    setSearchWindows(prev => prev.filter(w => w.id !== windowId));
  }, []);
  
  // Data source toggle: 'personal' or 'worldwide'
  const [dataSource, setDataSource] = useState('worldwide');
  
  // Category browser tab: 'my' or 'friends'
  const [categoryTab, setCategoryTab] = useState('my');
  
  // NEW: State for clicked category results (displayed in center panel)
  const [clickedCategoryResults, setClickedCategoryResults] = useState({});
  const [analyticsCategory, setAnalyticsCategory] = useState(null); // { categoryId: { name, results, loading } }
  
  // Drag-and-drop state for reordering results
  const [draggedResult, setDraggedResult] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  
  // Map state
  const [mapCenter, setMapCenter] = useState([39.8283, -98.5795]);
  const [dedicatedMapResults, setDedicatedMapResults] = useState([]);
  const [mapZoom] = useState(4);
  // Advanced visualization mode: 'standard' | 'heatmap' | 'fractal' | 'voronoi'
  const [searchVizMode, setSearchVizMode] = useState('standard');
  // Comparison mode: side-by-side independent viz
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonVizLeft, setComparisonVizLeft] = useState('standard');
  const [comparisonVizRight, setComparisonVizRight] = useState('fractal');
  const [dotColorMode, setDotColorMode] = useState('category');
  const [legendFilter, setLegendFilter] = useState(null);

  // Clear legend filter when color mode or viz mode changes
  React.useEffect(() => { setLegendFilter(null); }, [dotColorMode, searchVizMode]);
  const [filterInfo, setFilterInfo] = useState({ filter_applied: false, aggregation_mode: 'and_or' });
  const [refreshingLocations, setRefreshingLocations] = useState(false);
  const [locationStats, setLocationStats] = useState(null);
  
  const [showFilteredResults, setShowFilteredResults] = useState(true);
  
  // Educated Individual topic keywords for filtering
  const educatedIndividualKeywords = useMemo(() => ({
    science: ['chemistry', 'physics', 'biology', 'chemical', 'molecule', 'atom', 'reaction', 'experiment', 'laboratory', 'scientific'],
    engineering: ['engineering', 'electrical', 'mechanical', 'civil', 'structural', 'circuit', 'voltage', 'current', 'materials', 'stress', 'strain'],
    construction: ['framing', 'drywall', 'plumbing', 'hvac', 'roofing', 'foundation', 'concrete', 'lumber', 'stud', 'joist', 'insulation', 'window installation', 'door installation'],
    culinary: ['cooking', 'recipe', 'stock', 'sauce', 'baking', 'roast', 'braise', 'sauté', 'grill', 'smoke', 'meat', 'temperature'],
    trades: ['welding', 'carpentry', 'masonry', 'plumber', 'electrician', 'hvac tech', 'apprentice', 'journeyman', 'tool', 'workshop'],
    mathematics: ['calculus', 'algebra', 'geometry', 'trigonometry', 'equation', 'formula', 'theorem', 'proof', 'derivative', 'integral'],
    technology: ['programming', 'coding', 'software', 'hardware', 'network', 'database', 'algorithm', 'tutorial', 'arduino', 'raspberry pi']
  }), []);
  
  // Fetch admin-controlled min word count
  const [chatModulePosition, setChatModulePosition] = useState('above_windows');
  useEffect(() => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/admin/settings/public`, true);
    x.onload = function() {
      if (x.status === 200) {
        try {
          const data = JSON.parse(x.responseText);
          if (data.min_educated_individual_words) setMinWordCount(data.min_educated_individual_words);
          if (data.chat_module_position) setChatModulePosition(data.chat_module_position);
        } catch (e) {}
      }
    };
    x.send();
  }, []);

  // Category colors for map markers
  const categoryColors = useMemo(() => [
    '#FFE082', '#FFD54F', '#3b82f6', '#10b981', '#f59e0b', 
    '#ef4444', '#06b6d4', '#FFD700', '#84cc16', '#a855f7'
  ], []);

  // Get color for a category
  const getCategoryColor = useCallback((categoryId) => {
    const index = categories.findIndex(c => c.id === categoryId);
    return categoryColors[index % categoryColors.length];
  }, [categories, categoryColors]);

  // Category color map for PieChartMarkers (name → color)
  const categoryColorMap = useMemo(() => {
    const map = {};
    categories.forEach((cat, idx) => {
      map[cat.name] = CATEGORY_COLORS[idx % CATEGORY_COLORS.length];
    });
    return map;
  }, [categories]);

  // Results with location data for map - Use dedicated map data with server-side extraction
  // Falls back to search results with stored coordinates
  const mapResults = useMemo(() => {
    if (dedicatedMapResults.length > 0) return dedicatedMapResults;
    return searchResults.filter(r => r.latitude && r.longitude);
  }, [dedicatedMapResults, searchResults]);
  
  // Window overlay for map — single window from "Map" button click
  const [windowOverlay, setWindowOverlay] = useState(null);
  // Plotted windows from "Plot Map" checkboxes — { windowId: { title, results } }
  const [plottedWindows, setPlottedWindows] = useState({});
  
  // Listen for map events from URL Collection Windows
  useEffect(() => {
    const handleShowOnMap = (e) => {
      const { windowId, title, results: windowResults } = e.detail || {};
      if (windowResults && windowResults.length > 0) {
        setWindowOverlay({ windowId, title, results: windowResults });
      }
    };
    const handlePlotWindow = (e) => {
      const { windowId, title, results: windowResults } = e.detail || {};
      if (windowId && windowResults) {
        setPlottedWindows(prev => ({ ...prev, [windowId]: { title, results: windowResults } }));
      }
    };
    const handleRemoveWindow = (e) => {
      const { windowId } = e.detail || {};
      if (windowId) {
        setPlottedWindows(prev => { const next = { ...prev }; delete next[windowId]; return next; });
      }
    };
    window.addEventListener('showWindowOnMap', handleShowOnMap);
    window.addEventListener('plotWindowOnMap', handlePlotWindow);
    window.addEventListener('removeWindowFromMap', handleRemoveWindow);
    return () => {
      window.removeEventListener('showWindowOnMap', handleShowOnMap);
      window.removeEventListener('plotWindowOnMap', handlePlotWindow);
      window.removeEventListener('removeWindowFromMap', handleRemoveWindow);
    };
  }, []);
  
  // Filtered results based on category and document type selections
  const filteredResults = useMemo(() => {
    let results = searchResults;
    
    // Filter by selected categories
    if (selectedCategories.length > 0) {
      const selectedCatNames = categories
        .filter(c => selectedCategories.includes(c.id))
        .map(c => c.name);
      
      if (aggregation === 'and') {
        // AND: Must have ALL selected categories
        results = results.filter(r => 
          selectedCatNames.every(catName => r.categories?.includes(catName))
        );
      } else {
        // OR / AND_OR: Must have ANY selected category
        results = results.filter(r => 
          selectedCatNames.some(catName => r.categories?.includes(catName))
        );
      }
    }
    
    // Filter by selected document types
    if (selectedDocTypes.length > 0) {
      results = results.filter(r => selectedDocTypes.includes(r.article_type));
    }
    
    // Filter by minimum quality score
    if (minQualityScore > 0) {
      results = results.filter(r => (r.content_quality_score || 50) >= minQualityScore);
    }
    
    // Apply Educated Individual filter - boost results matching educational topics
    const activeTopics = Object.entries(educatedIndividualFilters)
      .filter(([_, active]) => active)
      .map(([topic]) => topic);
    
    if (activeTopics.length > 0 && activeTopics.length < 7) {
      // If not all topics selected, filter to only matching topics
      results = results.filter(r => {
        const textToSearch = `${r.title || ''} ${r.snippet || ''} ${r.description || ''}`.toLowerCase();
        return activeTopics.some(topic => {
          const keywords = educatedIndividualKeywords[topic] || [];
          return keywords.some(kw => textToSearch.includes(kw));
        });
      });
    }
    
    // Apply min word count filter when Educated Individual Sites are active
    const hasActiveEITopics = Object.values(educatedIndividualFilters).some(v => v === true);
    if (hasActiveEITopics && minWordCount > 0) {
      results = results.filter(r => {
        const wordCount = r.word_count || r.content_length || 0;
        if (wordCount > 0) return wordCount >= minWordCount;
        const snippetWords = (r.snippet || '').split(/\s+/).length;
        return snippetWords >= 50;
      });
    }
    
    // Apply per-topic min word counts for Educated Individual Sites
    if (activeTopics.length > 0 && activeTopics.length < 7) {
      results = results.filter(r => {
        const wc = r.word_count || r.content_length || 0;
        const text = `${r.title || ''} ${r.snippet || ''}`.toLowerCase();
        for (const topic of activeTopics) {
          const kws = educatedIndividualKeywords[topic] || [];
          if (kws.some(kw => text.includes(kw))) {
            const topicMin = eiMinWords[topic] || 0;
            if (topicMin > 0 && wc > 0 && wc < topicMin) return false;
          }
        }
        return true;
      });
    }
    
    // Apply per-document-type min word counts
    results = results.filter(r => {
      if (!r.article_type) return true;
      const dtMin = docTypeMinWords[r.article_type] || 0;
      if (dtMin <= 0) return true;
      const wc = r.word_count || r.content_length || 0;
      if (wc > 0) return wc >= dtMin;
      return true;
    });
    
    // Apply per-certification min word counts
    const activeCertKeysForWC = Object.entries(certificationFilters).filter(([, v]) => v).map(([k]) => k);
    if (activeCertKeysForWC.length > 0 && activeCertKeysForWC.length < 3) {
      results = results.filter(r => {
        const wc = r.word_count || r.content_length || 0;
        for (const ck of activeCertKeysForWC) {
          const certMin = certMinWords[ck] || 0;
          if (certMin > 0 && wc > 0 && wc < certMin) return false;
        }
        return true;
      });
    }
    
    // Favor Pictures: boost results with image/picture indicators
    if (educatedIndividualFilters.favor_pictures) {
      const pictureKeywords = ['image', 'photo', 'picture', 'gallery', 'illustration', 'figure', 'img', 'photograph', 'infographic'];
      results.sort((a, b) => {
        const aText = `${a.title || ''} ${a.snippet || ''} ${a.url || ''}`.toLowerCase();
        const bText = `${b.title || ''} ${b.snippet || ''} ${b.url || ''}`.toLowerCase();
        const aScore = pictureKeywords.filter(kw => aText.includes(kw)).length;
        const bScore = pictureKeywords.filter(kw => bText.includes(kw)).length;
        return bScore - aScore;
      });
    }
    
    // Apply certification filters
    const activeCertKeys = Object.entries(certificationFilters).filter(([, v]) => v).map(([k]) => k);
    const certKeywordMap = {
      pearsonvue: ['pearsonvue', 'pearson vue', 'certification exam', 'prometric', 'comptia', 'cisco certified'],
      government: ['government', '.gov', 'federal', 'state agency', 'municipal', 'public sector', 'department of'],
      advancedDegree: ['ph.d', 'phd', 'doctorate', 'master of science', 'master of arts', 'dissertation', 'thesis', 'postdoctoral']
    };
    if (activeCertKeys.length > 0 && activeCertKeys.length < 3) {
      results = results.filter(r => {
        const text = `${r.title || ''} ${r.snippet || ''} ${r.url || ''} ${r.article_type || ''}`.toLowerCase();
        return activeCertKeys.some(key => {
          const kws = certKeywordMap[key] || [];
          return kws.some(kw => text.includes(kw));
        });
      });
    }
    
    return results;
  }, [searchResults, selectedCategories, selectedDocTypes, categories, aggregation, minQualityScore, educatedIndividualFilters, educatedIndividualKeywords, minWordCount, certificationFilters, eiMinWords, docTypeMinWords, certMinWords]);
  
  // Filtered map results - applies ALL the same filters as filteredResults plus location requirement
  const filteredMapResults = useMemo(() => {
    // If a single window overlay is active (Map button click), show only its results
    if (windowOverlay) {
      return (windowOverlay.results || []).filter(r => r.latitude && r.longitude);
    }
    
    // If URL Collection Windows are plotted, show ONLY their results (not old DB data)
    const plottedEntries = Object.values(plottedWindows);
    if (plottedEntries.length > 0) {
      const plottedResults = [];
      const seen = new Set();
      plottedEntries.forEach(win => {
        const winLabel = win.title || 'Search Results';
        (win.results || []).forEach(r => {
          if (r.latitude && r.longitude && !seen.has(r.url)) {
            seen.add(r.url);
            // Override categories with window title so map legend shows search context
            plottedResults.push({ ...r, categories: [winLabel] });
          }
        });
      });
      return plottedResults;
    }
    
    // No windows plotted — fall back to full DB map data with filters
    let results = mapResults;
    
    // Apply category filters
    if (selectedCategories.length > 0) {
      const selectedCatNames = categories
        .filter(c => selectedCategories.includes(c.id))
        .map(c => c.name);
      if (aggregation === 'and') {
        results = results.filter(r => selectedCatNames.every(catName => r.categories?.includes(catName)));
      } else {
        results = results.filter(r => selectedCatNames.some(catName => r.categories?.includes(catName)));
      }
    }
    
    // Apply doc type filters
    if (selectedDocTypes.length > 0) {
      results = results.filter(r => selectedDocTypes.includes(r.article_type));
    }
    
    // Apply quality filters
    if (minQualityScore > 0) {
      results = results.filter(r => (r.content_quality_score || 50) >= minQualityScore);
    }
    
    let withLocation = results.filter(r => r.latitude && r.longitude);
    
    // Merge plotted window results (from "Plot Map" checkboxes)
    const plottedResults = [];
    Object.values(plottedWindows).forEach(win => {
      (win.results || []).forEach(r => {
        if (r.latitude && r.longitude) plottedResults.push(r);
      });
    });
    if (plottedResults.length > 0) {
      const seen = new Set(withLocation.map(r => r.url));
      const extra = plottedResults.filter(r => !seen.has(r.url));
      withLocation = [...withLocation, ...extra];
    }
    
    return withLocation;
  }, [mapResults, selectedCategories, categories, aggregation, selectedDocTypes, minQualityScore, windowOverlay, plottedWindows]);
  
  // Toggle document type selection
  const toggleDocType = (docType) => {
    setSelectedDocTypes(prev => 
      prev.includes(docType)
        ? prev.filter(d => d !== docType)
        : [...prev, docType]
    );
    // Show filtered results when filters are active
    setShowFilteredResults(true);
  };

  // Handle applying a template
  const handleApplyTemplate = (protocol, templateName) => {
    setNewCategory({ ...newCategory, protocol: protocol, name: templateName });
    setShowCategoryModal(true);
  };

  // === API CALLS ===
  
  const fetchBatches = useCallback(() => {
    return new Promise((resolve) => {
      xhr.get('/ultimate-search/batches', {
        onSuccess: (data) => { if (isMountedRef.current) setBatches(data.batches || []); },
        onFinally: () => resolve()
      }).catch(() => resolve());
    });
  }, [xhr]);

  const deleteBatch = (batchId) => {
    if (!window.confirm('Delete all results from this search session? This cannot be undone.')) return;
    xhr.del(`/ultimate-search/batch/${batchId}`, {
      onSuccess: (data) => {
        showToast(`Deleted ${data.deleted_count} results`, 'success');
        fetchBatches(); fetchSearchResults(); triggerMapRefresh();
      },
      onError: (errMsg) => { showToast(errMsg || 'Failed to delete batch', 'error'); }
    });
  };

  const deleteResult = (resultId) => {
    xhr.del(`/ultimate-search/result/${resultId}`, {
      onSuccess: () => { showToast('Result deleted', 'success'); fetchSearchResults(); triggerMapRefresh(); },
      onError: (errMsg) => { showToast(errMsg || 'Failed to delete result', 'error'); }
    });
  };

  // Handle category title click - opens a new URL Collection Window at the top of Search Results Area
  const handleCategoryTitleClick = useCallback((category) => {
    const categoryId = category.id || category._id;
    const categoryName = category.name;
    
    // Check if already open as a search window — avoid duplicates
    const alreadyOpen = searchWindows.some(w => w.id === `cat-${categoryId}`);
    if (alreadyOpen) {
      showToast(`"${categoryName}" is already open as a URL Collection Window`, 'info');
      return;
    }
    
    // Create a placeholder window while loading
    const windowId = `cat-${categoryId}`;
    setSearchWindows(prev => [{
      id: windowId,
      title: categoryName,
      searchType: 'Category',
      results: [],
      accentColor: '#FFD700',
      loading: true
    }, ...prev].slice(0, 60));
    
    xhr.get(`/ultimate-search?category_ids=${categoryId}&limit=100`, {
      onSuccess: (data) => {
        const results = data.results || data || [];
        setSearchWindows(prev => prev.map(w => 
          w.id === windowId ? { ...w, results, loading: false } : w
        ));
        showToast(results.length > 0 
          ? `Loaded ${results.length} URLs from "${categoryName}"` 
          : `No results in "${categoryName}" yet. Run a search first!`, 
          results.length > 0 ? 'success' : 'info');
      },
      onError: () => {
        setSearchWindows(prev => prev.filter(w => w.id !== windowId));
        showToast(`Failed to load "${categoryName}"`, 'error');
      }
    });
  }, [xhr, searchWindows, showToast]);
  
  // Clear all clicked category results
  const clearClickedCategoryResults = useCallback(() => {
    setClickedCategoryResults({});
  }, []);
  
  const fetchLocationStats = useCallback(() => {
    if (!token) return;
    xhr.get('/search/location-stats', {
      onSuccess: (data) => { setLocationStats(data); }
    });
  }, [token, xhr]);
  
  // Combine all clicked category results for display
  const combinedClickedResults = useMemo(() => {
    const allResults = [];
    Object.entries(clickedCategoryResults).forEach(([catId, data]) => {
      if (data.results && !data.loading) {
        data.results.forEach(r => {
          allResults.push({
            ...r,
            fromCategory: data.name,
            fromCategoryId: catId
          });
        });
      }
    });
    return allResults;
  }, [clickedCategoryResults]);

  // State for manually ordered results (supports drag-and-drop)
  const [orderedResults, setOrderedResults] = useState([]);
  
  // Sync orderedResults when combinedClickedResults changes
  useEffect(() => {
    setOrderedResults(combinedClickedResults);
  }, [combinedClickedResults]);
  
  // Drag and drop handlers
  const handleDragStart = useCallback((e, result, index) => {
    setDraggedResult({ result, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  }, []);
  
  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);
  
  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);
  
  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    if (draggedResult === null) return;
    
    const { index: dragIndex } = draggedResult;
    if (dragIndex === dropIndex) {
      setDraggedResult(null);
      setDragOverIndex(null);
      return;
    }
    
    // Reorder the results
    const newResults = [...orderedResults];
    const [movedItem] = newResults.splice(dragIndex, 1);
    newResults.splice(dropIndex, 0, movedItem);
    
    setOrderedResults(newResults);
    setDraggedResult(null);
    setDragOverIndex(null);
    showToast('Results reordered', 'success');
  }, [draggedResult, orderedResults, showToast]);
  
  const handleDragEnd = useCallback(() => {
    setDraggedResult(null);
    setDragOverIndex(null);
  }, []);

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  const initialLoadDone = useRef(false);
  
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  // Fetch categories using XMLHttpRequest to avoid Emergent platform fetch interception
  const fetchCategories = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!token) { resolve(); return; }
      xhr.get('/dashboard-init?mine_only=true', {
        onSuccess: (data) => {
          const cats = data.categories || [];
          if (isMountedRef.current) setCategories(cats);
          if (data.analytics) { window.__analyticsCache = data.analytics; updateAnalyticsCache(data.analytics); }
          resolve(cats);
        },
        onError: (err) => { reject(new Error(err)); }
      }).catch(reject);
    });
  }, [token, xhr]);

  // Refresh location data for all results - defined after fetchCategories to avoid circular dependency
  const refreshLocations = useCallback(() => {
    if (!token) return;
    setRefreshingLocations(true);
    xhr.post('/search/refresh-locations', {
      body: {},
      onSuccess: (data) => { showToast(`Updated ${data.updated_with_location} results with location data`, 'success'); fetchCategories(); },
      onFinally: () => { setRefreshingLocations(false); }
    });
  }, [token, xhr, showToast, fetchCategories]);

  const fetchSearchResults = useCallback(() => {
    return new Promise((resolve) => {
      const params = new URLSearchParams({ aggregation, limit: 2000, scope: dataSource || 'worldwide' });
      if (selectedCategories.length > 0) params.append('category_ids', selectedCategories.join(','));
      xhr.get(`/ultimate-search?${params}`, {
        onSuccess: (data) => {
          if (isMountedRef.current) {
            setSearchResults(data.results || []);
            setFilterInfo({ filter_applied: data.filter_applied || false, aggregation_mode: data.aggregation_mode || 'and_or', count: data.count || 0, total: data.total || data.count || 0 });
          }
          resolve(data);
        },
        onError: () => resolve(null)
      }).catch(() => resolve(null));
    });
  }, [xhr, selectedCategories, aggregation, dataSource]);

  // Fetch map data with server-side location extraction (called by Refresh button)
  const fetchMapData = useCallback(async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/map/worldwide?personal=true&limit=5000`;
      const resp = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (isMountedRef.current) {
        setDedicatedMapResults(data.results || []);
      }
    } catch (e) {
      // ignore
    }
  }, [token]);

  // Clean / Clear map dots
  const [cleaningDots, setCleaningDots] = useState(false);
  const [clearingDots, setClearingDots] = useState(false);
  const [cleanProgress, setCleanProgress] = useState('');

  const cleanMapDots = useCallback(async () => {
    setCleaningDots(true);
    setCleanProgress('Re-verifying all URLs against protocols...');
    try {
      const res = await new Promise((resolve, reject) => {
        xhr.post('/map/clean-dots', {
          body: {},
          onSuccess: resolve,
          onError: reject
        });
      });
      setCleanProgress(res.message || 'Done!');
      showToast?.(`Cleaned: ${res.category_assignments_removed} mismatched removed, ${res.category_assignments_kept} kept`, 'success');
      fetchMapData();
      fetchSearchResults();
      fetchCategories();
    } catch {
      setCleanProgress('Error during cleaning.');
      showToast?.('Failed to clean map dots', 'error');
    }
    setCleaningDots(false);
  }, [xhr, fetchMapData, fetchSearchResults, fetchCategories, showToast]);

  const [clearConfirmPending, setClearConfirmPending] = useState(false);

  const clearAllDots = useCallback(async () => {
    if (!clearConfirmPending) {
      setClearConfirmPending(true);
      showToast?.('Click "Clear All Dots" again to confirm. This will delete ALL search results.', 'info');
      // Auto-reset after 5 seconds if not confirmed
      setTimeout(() => setClearConfirmPending(false), 5000);
      return;
    }
    setClearConfirmPending(false);
    setClearingDots(true);
    setCleanProgress('Clearing all dots...');
    try {
      const res = await new Promise((resolve, reject) => {
        xhr.post('/map/clear-all-dots', {
          body: {},
          onSuccess: resolve,
          onError: reject
        });
      });
      setCleanProgress(res.message || 'Done!');
      showToast?.(`Cleared ${res.deleted} results. Fresh map ready!`, 'success');
      setDedicatedMapResults([]);
      fetchMapData();
      fetchSearchResults();
      fetchCategories();
    } catch {
      setCleanProgress('Error during clearing.');
      showToast?.('Failed to clear dots', 'error');
    }
    setClearingDots(false);
  }, [xhr, fetchMapData, fetchSearchResults, fetchCategories, showToast]);

  // Refresh map dots — batch geocode all URLs
  const [refreshingDots, setRefreshingDots] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState('');

  const refreshMapDots = useCallback(async () => {
    setRefreshingDots(true);
    setRefreshProgress('Starting...');
    let totalGeo = 0;
    let calls = 0;
    const maxCalls = 50; // Safety limit
    while (calls < maxCalls) {
      calls++;
      try {
        const res = await new Promise((resolve, reject) => {
          xhr.post('/map/refresh-dots', {
            body: {},
            onSuccess: resolve,
            onError: reject
          });
        });
        totalGeo += (res.text_geocoded || 0) + (res.ip_geocoded || 0);
        setRefreshProgress(`Processed ${res.processed} URLs (${res.text_geocoded} text + ${res.ip_geocoded} IP). ${res.remaining} remaining...`);
        if (res.remaining === 0 || res.processed === 0) break;
      } catch {
        setRefreshProgress('Error during refresh. Retrying...');
        break;
      }
    }
    setRefreshProgress(`Done! Geocoded ${totalGeo} URLs total.`);
    showToast?.(`Refresh complete: ${totalGeo} URLs geocoded`, 'success');
    fetchMapData();
    setRefreshingDots(false);
  }, [xhr, fetchMapData, showToast]);


  useEffect(() => {
    if (!token) return;
    // Prevent double-loading from StrictMode double-mounting
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    
    // Load data sequentially. Analytics is now included in the dashboard-init response.
    fetchCategories()
      .then(() => fetchSearchResults())
      .then(() => fetchBatches())
      .then(() => {
        // fetchMapData is fire-and-forget (no return promise), so call it explicitly
        fetchMapData();
      })
      .catch((e) => console.error('Error during data load:', e));
  }, [token, fetchCategories, fetchSearchResults, fetchBatches, fetchMapData]);

  // Fetch ALL map data once on mount using native fetch (no filters — filter client-side)
  const mapDataLoaded = useRef(false);
  useEffect(() => {
    if (!token || mapDataLoaded.current) return;
    mapDataLoaded.current = true;
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/map/worldwide?personal=true&limit=5000`;
        const resp = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!resp.ok) { mapDataLoaded.current = false; return; }
        const data = await resp.json();
        setDedicatedMapResults(data.results || []);
      } catch (e) {
        mapDataLoaded.current = false;
      }
    })();
  }, [token]);

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    const startTime = Date.now();
    xhr.post('/search', {
      body: { query: searchQuery },
      onSuccess: (searchData) => {
        const totalTime = ((Date.now() - startTime) / 1000).toFixed(1);
        addSearchWindow(searchQuery, 'Quick Search', searchData.results || [], '#3b82f6');
        showToast(`Found ${searchData.total || 0} results in ${totalTime}s!`, 'success');
        fetchSearchResults(); fetchBatches(); triggerMapRefresh(); fetchMapData();
      },
      onError: () => { showToast('Search failed', 'error'); },
      onFinally: () => { setLoading(false); }
    }).catch(() => { setLoading(false); });
  };

  // AUTO-CATEGORIZE: Background task that polls for results
  const [autoCatLoading, setAutoCatLoading] = useState(false);
  const [autoCatTaskId, setAutoCatTaskId] = useState(null);
  const autoCatPollRef = useRef(null);

  // Poll for auto-categorize task completion
  const pollTaskStatus = useCallback((taskId, startTime) => {
    if (autoCatPollRef.current) autoCatPollRef.current(); // cancel previous
    
    // Safety timeout: cancel polling after 5 minutes
    const safetyTimeout = setTimeout(() => {
      if (autoCatPollRef.current) {
        autoCatPollRef.current();
        autoCatPollRef.current = null;
      }
      setAutoCatLoading(false);
      setAutoCatTaskId(null);
      showToast('Auto-categorize is taking too long. Check task history for status.', 'warning');
    }, 300000);
    
    autoCatPollRef.current = xhr.poll(`/task-status/${taskId}`, {
      interval: 2000,
      until: (data) => data.status === 'completed' || data.status === 'failed',
      onDone: (task) => {
        clearTimeout(safetyTimeout);
        autoCatPollRef.current = null;
        setAutoCatLoading(false);
        setAutoCatTaskId(null);
        
        if (task.status === 'completed') {
          const data = task.result || {};
          const totalTime = ((Date.now() - startTime) / 1000).toFixed(1);
          setLastBatchId(data.batch_id);
          if (data.category_summary?.length > 0) setSelectedCategories(data.category_summary.map(c => c.id));
          addSearchWindow(searchQuery || task.query || 'Auto Categorize', 'Auto Categorize All', data.results || [], '#f59e0b');
          showToast(`${data.message || 'Auto-categorize complete'} (${totalTime}s)`, 'success');
          fetchSearchResults(); fetchBatches(); triggerMapRefresh(); fetchMapData();
        } else {
          showToast(`Auto-categorize failed: ${task.error || 'Unknown error'}`, 'error');
        }
      }
    });
  }, [xhr, searchQuery, addSearchWindow, showToast, fetchSearchResults, fetchBatches, fetchMapData]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (autoCatPollRef.current) clearInterval(autoCatPollRef.current);
    };
  }, []);

  const autoCategorizeSearc = () => {
    if (!searchQuery.trim()) { showToast('Please enter a search query', 'error'); return; }
    setAutoCatLoading(true);
    const startTime = Date.now();
    xhr.post('/auto-categorize', {
      body: { query: searchQuery, exact_match: exactMatch, search_depth: searchDepth },
      onSuccess: (data) => {
        if (data.task_id) {
          setAutoCatTaskId(data.task_id);
          showToast('Auto-categorize started! Processing in background...', 'info');
          pollTaskStatus(data.task_id, startTime);
        } else {
          const totalTime = ((Date.now() - startTime) / 1000).toFixed(1);
          setLastBatchId(data.batch_id);
          if (data.category_summary?.length > 0) setSelectedCategories(data.category_summary.map(c => c.id));
          addSearchWindow(searchQuery, 'Auto Categorize All', data.results || [], '#f59e0b');
          showToast(`${data.message} (${totalTime}s)`, 'success');
          fetchSearchResults(); fetchBatches(); triggerMapRefresh();
          setAutoCatLoading(false);
        }
      },
      onError: (errMsg) => { showToast(errMsg || 'Auto-categorization failed', 'error'); setAutoCatLoading(false); }
    }).catch(() => { setAutoCatLoading(false); });
  };

  // AI INTELLIGENT SEARCH
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchMode, setAiSearchMode] = useState('comprehensive');
  
  const aiIntelligentSearch = () => {
    if (!searchQuery.trim()) { showToast('Please enter a search query', 'error'); return; }
    setAiSearchLoading(true);
    const startTime = Date.now();
    xhr.post('/ai-search', {
      body: { query: searchQuery, mode: aiSearchMode, auto_categorize: true, exact_match: exactMatch },
      onSuccess: (data) => {
        const totalTime = ((Date.now() - startTime) / 1000).toFixed(1);
        setLastBatchId(data.batch_id);
        addSearchWindow(searchQuery, 'AI Search & Categorize', data.results || [], '#10b981');
        showToast(`${data.message} (${totalTime}s)`, 'success');
        fetchSearchResults(); fetchBatches(); triggerMapRefresh();
      },
      onError: (errMsg) => { showToast(errMsg || 'AI Search failed', 'error'); },
      onFinally: () => { setAiSearchLoading(false); }
    }).catch(() => { setAiSearchLoading(false); });
  };

  // DATABASE TEXT SEARCH - Search within already categorized results
  const [dbSearchLoading, setDbSearchLoading] = useState(false);
  const [dbSearchMode, setDbSearchMode] = useState('smart');
  
  // SEARCH ENGINE ACCESS - Direct search from all search engines
  const [searchEngineAccessLoading, setSearchEngineAccessLoading] = useState(false);
  const [searchEngineResults, setSearchEngineResults] = useState([]);
  
  const databaseTextSearch = () => {
    if (!searchQuery.trim()) { showToast('Please enter a search query', 'error'); return; }
    setDbSearchLoading(true);
    const startTime = Date.now();
    xhr.post('/database-search', {
      body: { query: searchQuery, mode: dbSearchMode, category_ids: selectedCategories, limit: 100 },
      onSuccess: (data) => {
        const totalTime = ((Date.now() - startTime) / 1000).toFixed(1);
        if (data.results?.length > 0) {
          addSearchWindow(searchQuery, 'Database Text Search', data.results, '#3b82f6');
          showToast(`${data.message} (${totalTime}s) - Found in ${data.total_in_database} total records`, 'success');
        } else {
          showToast('No matches found in database. Try running a search first to categorize results!', 'info');
        }
      },
      onError: (errMsg) => { showToast(errMsg || 'Database search failed', 'error'); },
      onFinally: () => { setDbSearchLoading(false); }
    }).catch(() => { setDbSearchLoading(false); });
  };

  // SEARCH ENGINE ACCESS - Direct search across all available search engines
  const searchEngineAccessSearch = () => {
    if (!searchQuery.trim()) { showToast('Please enter a search query', 'error'); return; }
    setSearchEngineAccessLoading(true);
    const startTime = Date.now();
    xhr.post('/search-engine-access', {
      body: { query: searchQuery, engines: ['google', 'bing', 'duckduckgo', 'brave'], limit: 100 },
      onSuccess: (data) => {
        const totalTime = ((Date.now() - startTime) / 1000).toFixed(1);
        addSearchWindow(searchQuery, 'Search Engine Access', data.results || [], '#06b6d4');
        setSearchEngineResults(data.results || []);
        showToast(`${data.message} (${totalTime}s)`, 'success');
      },
      onError: (errMsg) => { showToast(errMsg || 'Search Engine Access failed', 'error'); },
      onFinally: () => { setSearchEngineAccessLoading(false); }
    }).catch(() => { setSearchEngineAccessLoading(false); });
  };

  const categorizeWithCategories = () => {
    if (!searchQuery.trim()) { showToast('Please enter a search query', 'error'); return; }
    if (selectedCategories.length === 0) { autoCategorizeSearc(); return; }
    
    setCategorizeLoading(true);
    const startTime = Date.now();
    const taskIds = [];
    
    const launchNext = (index) => {
      if (index >= selectedCategories.length) {
        showToast(`Launched ${taskIds.length} categorization tasks. Processing in background...`, 'info');
        pollCategorizeTasks(taskIds, startTime);
        return;
      }
      xhr.post('/categorize', {
        body: { category_id: selectedCategories[index], aggregation, query: searchQuery, exact_match: exactMatch },
        onSuccess: (data) => { if (data.task_id) taskIds.push({ taskId: data.task_id, categoryId: selectedCategories[index] }); },
        onFinally: () => launchNext(index + 1)
      }).catch(() => launchNext(index + 1));
    };
    launchNext(0);
  };

  // Poll multiple categorize tasks for completion
  const pollCategorizeTasks = useCallback((tasks, startTime) => {
    if (!tasks.length) { setCategorizeLoading(false); return; }
    const completed = {};
    const allResults = [];
    let totalCategorized = 0;

    const poller = setInterval(() => {
      const pending = tasks.filter(t => !completed[t.taskId]);
      if (pending.length === 0) {
        clearInterval(poller);
        const totalTime = ((Date.now() - startTime) / 1000).toFixed(1);
        addSearchWindow(searchQuery, 'Search & Categorize', allResults, '#FFD54F');
        showToast(`Categorized ${totalCategorized} results across ${tasks.length} categories in ${totalTime}s!`, 'success');
        fetchSearchResults(); fetchBatches(); triggerMapRefresh();
        setCategorizeLoading(false);
        return;
      }
      
      const task = pending[0];
      xhr.get(`/task-status/${task.taskId}`, {
        onSuccess: (data) => {
          if (data.status === 'completed') {
            completed[task.taskId] = true;
            const result = data.result || {};
            totalCategorized += result.total || 0;
            if (result.results) allResults.push(...result.results);
            if (result.batch_id) setLastBatchId(result.batch_id);
          } else if (data.status === 'failed') {
            completed[task.taskId] = true;
          }
        }
      }).catch(() => { completed[task.taskId] = true; });
    }, 2000);

    setTimeout(() => { clearInterval(poller); setCategorizeLoading(false); }, 300000);
  }, [xhr, searchQuery, addSearchWindow, showToast, fetchSearchResults, fetchBatches]);

  const createCategory = async () => {
    if (!newCategory.name) { showToast('Category name is required', 'error'); return; }
    if (!newCategory.protocol) { showToast('Protocol is required (e.g., "aviation" or "(aviation or pilot)")', 'error'); return; }
    if (!token) { showToast('You must be logged in to create categories', 'error'); return; }
    
    const categoryName = newCategory.name;
    setShowCategoryModal(false);
    showToast('Creating category...', 'info');
    
    try {
      const result = await xhr.post('/categories', {
        body: { name: newCategory.name, protocol: newCategory.protocol, parent_id: newCategory.parent_id || null, privacy: newCategory.privacy || 'public' }
      });
      showToast(`Category "${categoryName}" created successfully!`, 'success');
      setNewCategory({ name: '', protocol: '', parent_id: null, privacy: 'public' });
      
      // Optimistic update: add new category to state immediately
      if (result && result.id) {
        setCategories(prev => {
          const exists = prev.some(c => c.id === result.id);
          if (exists) return prev;
          return [...prev, {
            id: result.id,
            name: result.name || categoryName,
            protocol: result.protocol || newCategory.protocol,
            parent_id: result.parent_id || newCategory.parent_id || null,
            level: result.level || 0,
            is_public: result.is_public || false,
            privacy: result.privacy || newCategory.privacy || 'public',
            url_count: 0
          }];
        });
      }
      
      // Also refresh from server to get accurate data (cache now invalidated)
      fetchCategories();
    } catch (err) {
      showToast(typeof err === 'string' ? err : 'Failed to create category', 'error');
      setShowCategoryModal(true);
    }
  };

  const saveProtocol = async () => {
    if (!editingCategory) return;
    const categoryId = editingCategory.id || editingCategory._id;
    if (!categoryId) { showToast('Category ID not found', 'error'); return; }
    xhr.put(`/categories/${categoryId}`, {
      body: { name: editCategoryName, protocol: editProtocol, privacy: editPrivacy },
      onSuccess: () => { showToast('Category updated successfully!', 'success'); fetchCategories(); setEditingCategory(null); },
      onError: (errMsg) => { showToast(errMsg || 'Failed to update category', 'error'); }
    });
  };

  const deleteCategory = (categoryId) => {
    xhr.del(`/categories/${categoryId}`, {
      onSuccess: () => {
        showToast('Category deleted successfully!', 'success');
        setEditingCategory(null);
        setSelectedCategories(prev => prev.filter(id => id !== categoryId));
        fetchCategories(); fetchSearchResults(); triggerMapRefresh();
      },
      onError: (errMsg) => { showToast(errMsg || 'Failed to delete category', 'error'); }
    });
  };

  const cleanCategory = (categoryId, mode = 'remove') => {
    xhr.post(`/categories/${categoryId}/clean?mode=${mode}`, {
      onSuccess: (data) => {
        showToast(data.message || 'Category cleaned successfully!', 'success');
        setEditingCategory(null);
        fetchCategories(); fetchSearchResults(); triggerMapRefresh();
      },
      onError: (errMsg) => { showToast(errMsg || 'Failed to clean category', 'error'); }
    });
  };

  const toggleCategorySelection = (catId) => {
    setSelectedCategories(prev => {
      const newSelection = prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId];
      // Automatically show filtered results when categories are selected
      if (newSelection.length > 0) {
        setShowFilteredResults(true);
      }
      return newSelection;
    });
  };

  const addReaction = (resultId, reactionType) => {
    xhr.post('/reactions', {
      body: { search_result_id: resultId, reaction_type: reactionType },
      onSuccess: () => { fetchSearchResults(); }
    });
  };


  // Semantic Search (Elasticsearch) functions
  const handleSemanticSearch = async () => {
    if (!esQuery.trim()) return;
    setEsLoading(true);
    try {
      const res = await xhr.post('/search/semantic', { body: { query: esQuery, size: 30 } });
      setEsResults(res.results || []);
      setEsTotal(res.total || 0);
      if (res.error) showToast?.(`ES: ${res.error}`, 'warning');
    } catch (e) {
      showToast?.('Semantic search failed', 'error');
    }
    setEsLoading(false);
  };

  const handleEsSync = async () => {
    setEsSyncing(true);
    try {
      const res = await xhr.post('/search/es-sync', { body: {} });
      if (res.task_id) {
        showToast?.(`Syncing ${res.total || ''} results to Elasticsearch...`, 'info');
        // Poll for completion
        const pollEs = setInterval(async () => {
          try {
            const status = await xhr.get(`/task-status/${res.task_id}`);
            if (status.status === 'completed') {
              clearInterval(pollEs);
              const r = status.result || {};
              showToast?.(`Synced ${r.indexed || 0} results to Elasticsearch (${r.errors || 0} errors)`, r.errors > 0 ? 'warning' : 'success');
              setEsSyncing(false);
            } else if (status.status === 'failed') {
              clearInterval(pollEs);
              showToast?.(`Sync failed: ${status.error || 'Unknown error'}`, 'error');
              setEsSyncing(false);
            }
          } catch { /* keep polling */ }
        }, 3000);
        setTimeout(() => { clearInterval(pollEs); setEsSyncing(false); }, 120000);
      } else {
        showToast?.(`Synced ${res.indexed || 0} results to Elasticsearch`, 'success');
        setEsSyncing(false);
      }
    } catch (e) {
      showToast?.(typeof e === 'string' ? e : 'Sync failed — Elasticsearch may not be available', 'error');
      setEsSyncing(false);
    }
  };

  // Smart Duplicate Detector
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);

  const loadDuplicates = async () => {
    setDupLoading(true);
    try {
      const res = await xhr.get('/search/duplicates');
      setDuplicates(res.duplicates || []);
      showToast?.(`Found ${res.total_duplicates} duplicate groups`, 'info');
    } catch (e) {
      showToast?.('Failed to detect duplicates', 'error');
    }
    setDupLoading(false);
  };

  // Per-Window Filter History
  const saveFilterState = useCallback(async (windowId) => {
    try {
      await xhr.post('/search/filter-history', {
        body: {
          window_id: windowId || 'default',
          filters: {
            selectedCategories: selectedCategories?.map(c => c.name || c),
            selectedDocTypes,
            minQualityScore,
            educatedIndividualFilters
          }
        }
      });
    } catch {}
  }, [xhr, selectedCategories, selectedDocTypes, minQualityScore, educatedIndividualFilters]);

  // Auto-save filters when they change
  useEffect(() => {
    const t = setTimeout(() => saveFilterState('main'), 2000);
    return () => clearTimeout(t);
  }, [saveFilterState]);


  return {
    // Auth
    token, user,
    // XHR
    xhr,
    // Analytics cache
    updateAnalyticsCache,
    // Filter context
    filters,
    educatedIndividualFilters, setEducatedIndividualFilters,
    eiMinWords, setEiMinWords,
    minWordCount, setMinWordCount,
    documentTypes, selectedDocTypes, setSelectedDocTypes,
    docTypeMinWords, setDocTypeMinWords,
    certificationFilters, setCertificationFilters,
    certMinWords, setCertMinWords,
    contentMinWords, setContentMinWords,
    minQualityScore, setMinQualityScore,
    exactMatch, setExactMatch,
    searchDepth, setSearchDepth,
    aggregation, setAggregation,
    selectedCategories, setSelectedCategories,
    showControlPanel, setShowControlPanel,
    controlPanelLayout, setControlPanelLayout,
    filterHistory, getRecommendations,
    loadPreferences, exportFilters,
    undo, redo, canUndo, canRedo,
    // Core state
    categories, setCategories, searchResults, setSearchResults,
    searchQuery, setSearchQuery, loading, categorizeLoading,
    showProgrammerMode, setShowProgrammerMode,
    autoUpdateEnabled, setAutoUpdateEnabled,
    // Modal state
    showCategoryModal, setShowCategoryModal,
    newCategory, setNewCategory,
    editingCategory, setEditingCategory,
    editProtocol, setEditProtocol,
    editCategoryName, setEditCategoryName,
    editPrivacy, setEditPrivacy,
    shareCategory, setShareCategory,
    // UI toggles
    batches, setBatches, showBatchManager, setShowBatchManager,
    lastBatchId, setLastBatchId,
    showDebugger, setShowDebugger,
    showTemplates, setShowTemplates,
    showMap, setShowMap,
    showContentDump, setShowContentDump,
    // Search windows
    searchWindows, setSearchWindows, addSearchWindow, closeSearchWindow,
    // Semantic search
    esQuery, setEsQuery, esResults, setEsResults,
    esLoading, esTotal, showSemanticSearch, setShowSemanticSearch, esSyncing,
    // Data source
    dataSource, setDataSource,
    categoryTab, setCategoryTab,
    // Clicked category results
    clickedCategoryResults, setClickedCategoryResults,
    analyticsCategory, setAnalyticsCategory,
    // Drag-and-drop
    draggedResult, setDraggedResult,
    dragOverIndex, setDragOverIndex,
    // Map state
    mapCenter, setMapCenter,
    dedicatedMapResults, setDedicatedMapResults,
    mapZoom,
    searchVizMode, setSearchVizMode,
    comparisonMode, setComparisonMode,
    comparisonVizLeft, setComparisonVizLeft,
    comparisonVizRight, setComparisonVizRight,
    dotColorMode, setDotColorMode,
    legendFilter, setLegendFilter,
    filterInfo, setFilterInfo,
    refreshingLocations,
    locationStats,
    showFilteredResults, setShowFilteredResults,
    educatedIndividualKeywords,
    // Category management
    categoryColorMap, getCategoryColor, toggleCategorySelection,
    fetchCategories, fetchSearchResults,
    // CRUD
    createCategory, deleteCategory,
    // Search actions
    handleSearch, categorizeWithCategories, autoCategorizeSearc,
    aiIntelligentSearch, databaseTextSearch, searchEngineAccessSearch,
    autoCatLoading, aiSearchLoading, aiSearchMode, setAiSearchMode,
    dbSearchLoading, dbSearchMode, setDbSearchMode,
    searchEngineAccessLoading,
    // Chat module
    chatModulePosition, setChatModulePosition,
    // Map data
    mapResults, filteredMapResults, fetchMapData,
    // Reactions
    addReaction,
    // Semantic search actions
    handleSemanticSearch, handleEsSync,
    // Duplicates
    showDuplicates, setShowDuplicates, duplicates, dupLoading, loadDuplicates,
    // Filter state save
    saveFilterState,
    // Clean/clear/refresh dots
    cleaningDots, clearingDots, cleanProgress,
    cleanMapDots, clearAllDots, clearConfirmPending,
    refreshingDots, refreshProgress,
    refreshMapDots,
    // Templates
    handleApplyTemplate,
    // Window overlay & plotted windows
    windowOverlay, setWindowOverlay,
    plottedWindows, setPlottedWindows,
    // Filtered results (client-side)
    filteredResults,
    // Doc type toggle
    toggleDocType,
    // Batch management
    fetchBatches, deleteBatch,
    // Result management
    deleteResult,
    // Category title click & clicked results
    handleCategoryTitleClick, clearClickedCategoryResults,
    combinedClickedResults, orderedResults,
    // Drag-and-drop handlers
    handleDragStart, handleDragOver, handleDragLeave, handleDrop, handleDragEnd,
    // Search engine results
    searchEngineResults, setSearchEngineResults,
    // Protocol save & clean
    saveProtocol, cleanCategory,
  };
};

export { CATEGORY_COLORS };
export default useSearchPageData;
